/**
 * src/core/telemetry.js — Bloque H5: alarma de errores en producción (Sentry).
 *
 * Funciones puras (testeables) + el init guardado. La idea: que main.js solo
 * llame `initSentry(Sentry, {...})` dentro de un try/catch, y que toda la
 * lógica de environment + scrubbing de credenciales viva acá, cubierta por
 * tests (no se puede testear el envío real a Sentry, pero sí el filtrado).
 *
 * Privacidad: NUNCA se mandan a Sentry headers de auth (Authorization, apikey,
 * cookie), ni bodies con email/password/token, ni URLs de /auth/v1/ de
 * Supabase (que llevan tokens). `sendDefaultPii: false` además evita que el
 * SDK adjunte datos personales por su cuenta.
 */

// Headers que jamás deben viajar a Sentry (comparación case-insensitive).
export const AUTH_HEADER_KEYS = ['authorization', 'apikey', 'cookie', 'x-client-info'];

// Claves de body que delatan credenciales — se redactan si aparecen.
const CREDENTIAL_KEY_RE = /(pass|password|email|token|secret|apikey|authorization)/i;

const FILTERED = '[Filtered]';

/** Mapea el hostname al environment de Sentry. */
export const sentryEnvironment = (hostname = '') => {
  if (hostname === 'pactomundial.futbol' || hostname === 'www.pactomundial.futbol') return 'production';
  if (hostname.endsWith('.vercel.app')) return 'preview';
  return 'development';
};

/**
 * beforeSend: limpia credenciales del evento antes de mandarlo. Mutado in-place
 * y devuelto (contrato de Sentry: devolver el evento, o null para descartarlo).
 */
export const scrubEvent = (event) => {
  if (!event || typeof event !== 'object') return event;

  const req = event.request;
  if (req && typeof req === 'object') {
    // Headers de auth.
    if (req.headers && typeof req.headers === 'object') {
      for (const k of Object.keys(req.headers)) {
        if (AUTH_HEADER_KEYS.includes(k.toLowerCase())) req.headers[k] = FILTERED;
      }
    }
    // Body con credenciales (email/password/token).
    if (req.data && typeof req.data === 'object') {
      for (const k of Object.keys(req.data)) {
        if (CREDENTIAL_KEY_RE.test(k)) req.data[k] = FILTERED;
      }
    }
  }

  // Breadcrumbs con URLs de auth de Supabase (llevan tokens en query/fragment).
  if (Array.isArray(event.breadcrumbs)) {
    for (const b of event.breadcrumbs) {
      const url = b && b.data && b.data.url;
      if (typeof url === 'string' && /\/auth\/v1\//.test(url)) {
        b.data.url = '[Filtered auth URL]';
      }
    }
  }

  return event;
};

/**
 * Inicializa Sentry. Sin DSN no hace nada (devuelve false) — así en local sin
 * .env.local no se rompe ni se manda nada. `Sentry` se inyecta para testear.
 */
export const initSentry = (Sentry, { dsn, hostname = '', release } = {}) => {
  if (!dsn || !Sentry || typeof Sentry.init !== 'function') return false;
  Sentry.init({
    dsn,
    environment: sentryEnvironment(hostname),
    release: release || undefined,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    beforeSend: scrubEvent,
  });
  return true;
};
